/**
 * ServiceOrderItemsTab - Aba "Itens" do modal da O.S.: serviços do negócio e
 * serviços adicionados em campo (BR-OS-046..049).
 *
 * - Aviso "Serviços do negócio foram alterados" com "Atualizar do negócio" antes de iniciar;
 *   depois de iniciada, apenas informativo (cópia congelada).
 * - "Informar execução" abre a mesma janela usada no Concluir.
 * - Gestor aprova/recusa adicionais; nada é gravado no negócio.
 *
 * RELEASE: 2026-10-18 v1.1.0 - BR-OS-054: operador adiciona, edita (quantidade/orientação) e remove itens.
 * RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Check, ClipboardList, Info, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { InlineNotice } from '@/components/ui/inline-notice';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useServiceCatalogProducts, useServiceOrderServiceMutations, useServiceOrderServices } from '@/hooks/useServiceOrderServices';
import { SERVICE_APPROVAL_LABEL, SERVICE_EXECUTION_LABEL, canEditItemQuantity, servicesRequiringAnswer, type ServiceOrderServiceItem } from '@/lib/serviceOrders/serviceItems';
import { ServiceOrderServicesCompleteDialog } from './ServiceOrderServicesCompleteDialog';
import { ServiceOrderFieldServiceDialog } from './ServiceOrderFieldServiceDialog';

const msg = (e: unknown) => (e as { message?: string })?.message ?? 'Não foi possível concluir a ação';

const EXEC_TONE: Record<string, string> = {
  pending: 'bg-muted text-muted-foreground', done: 'bg-success/10 text-success', not_done: 'bg-destructive/10 text-destructive',
};
const APPROVAL_TONE: Record<string, string> = {
  pending: 'bg-warning/10 text-warning', approved: 'bg-success/10 text-success', rejected: 'bg-destructive/10 text-destructive',
};

interface Props {
  serviceOrderId: string;
  /** Pode editar a O.S. (atualizar, responder, adicionar, analisar) */
  canEdit: boolean;
}

export function ServiceOrderItemsTab({ serviceOrderId, canEdit }: Props) {
  const query = useServiceOrderServices(serviceOrderId);
  const m = useServiceOrderServiceMutations(serviceOrderId);
  const [answerOpen, setAnswerOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [rejecting, setRejecting] = useState<ServiceOrderServiceItem | null>(null);
  const [reason, setReason] = useState('');
  // BR-OS-054: inclusão, edição e remoção pelo operador
  const [manualOpen, setManualOpen] = useState(false);
  const [editing, setEditing] = useState<ServiceOrderServiceItem | null>(null);
  const [editQty, setEditQty] = useState('1');
  const [editNotes, setEditNotes] = useState('');
  const [removing, setRemoving] = useState<ServiceOrderServiceItem | null>(null);
  const products = useServiceCatalogProducts(addOpen || manualOpen);

  if (query.isLoading) return <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>;
  if (query.error) return <InlineNotice variant="error">{msg(query.error)}</InlineNotice>;
  const data = query.data!;
  const dealItems = data.items.filter((i) => i.origin === 'deal');
  const fieldItems = data.items.filter((i) => i.origin === 'field');
  const manualItems = data.items.filter((i) => i.origin === 'manual');
  const canManage = canEdit && !!data.can_manage;
  const canNotes = canEdit && !!data.can_edit_notes;
  const openEdit = (i: ServiceOrderServiceItem) => { setEditing(i); setEditQty(String(Number(i.quantity))); setEditNotes(i.operator_notes ?? ''); };
  /** Botões de editar/remover por item (só quando o status permite). */
  const itemActions = (i: ServiceOrderServiceItem) => (canNotes || canManage) ? (
    <div className="flex gap-0.5">
      <Button size="icon" variant="ghost" className="h-7 w-7" title="Editar item" onClick={() => openEdit(i)}><Pencil className="h-3.5 w-3.5" /></Button>
      {canEditItemQuantity(i, canManage) && <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" title="Remover item" onClick={() => setRemoving(i)}><Trash2 className="h-3.5 w-3.5" /></Button>}
    </div>
  ) : null;
  const editQtyNum = Number(editQty.replace(',', '.'));
  const editQtyAllowed = !!editing && canEditItemQuantity(editing, canManage);
  const toAnswer = servicesRequiringAnswer(data.items);

  const run = async (p: Promise<unknown>, ok: string) => { try { await p; toast.success(ok); return true; } catch (e) { toast.error(msg(e)); return false; } };

  return (
    <div className="space-y-4">
      {/* Cabeçalho com ações */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Serviços</h3>
          {data.deal && <span className="text-xs text-muted-foreground">Negócio {data.deal.code} · {data.deal.title}</span>}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {data.can_answer && toAnswer.length > 0 && <Button size="sm" variant="outline" onClick={() => setAnswerOpen(true)}><Check className="mr-1 h-3.5 w-3.5" />Informar execução</Button>}
            {canManage && <Button size="sm" variant="outline" onClick={() => setManualOpen(true)}><Plus className="mr-1 h-3.5 w-3.5" />Adicionar item</Button>}
            {data.can_answer && <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}><Plus className="mr-1 h-3.5 w-3.5" />Adicionar serviço</Button>}
          </div>
        )}
      </div>

      {/* BR-OS-049: alteração no negócio após a cópia */}
      {data.deal_changed && (
        <InlineNotice variant="warning">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>{data.can_refresh ? 'Serviços do negócio foram alterados.' : 'O negócio mudou após o início da O.S.; os serviços desta O.S. ficaram congelados.'}</span>
            {data.can_refresh && canEdit && (
              <Button size="sm" variant="outline" disabled={m.sync.isPending} onClick={() => run(m.sync.mutateAsync(), 'Serviços atualizados do negócio')}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" />Atualizar do negócio
              </Button>
            )}
          </div>
        </InlineNotice>
      )}

      {!data.deal && fieldItems.length === 0 && manualItems.length === 0 && (
        <InlineNotice variant="info">Nenhum negócio vinculado. Edite a O.S. e escolha o negócio para trazer os serviços.</InlineNotice>
      )}

      {dealItems.length > 0 && <ItemsList title="Do negócio" items={dealItems} renderActions={itemActions} />}
      {manualItems.length > 0 && <ItemsList title="Adicionados pelo operador" items={manualItems} renderActions={itemActions} />}
      {fieldItems.length > 0 && (
        <ItemsList title="Adicionados em campo" items={fieldItems}
          renderActions={(i) => canEdit && i.approval_status === 'pending' ? (
            <div className="flex gap-1">
              {itemActions(i)}
              <Button size="sm" variant="outline" disabled={m.review.isPending} onClick={() => run(m.review.mutateAsync({ serviceId: i.id, decision: 'approved' }), 'Serviço aprovado')}>Aprovar</Button>
              <Button size="sm" variant="outline" onClick={() => { setRejecting(i); setReason(''); }}>Recusar</Button>
            </div>
          ) : itemActions(i)} />
      )}

      <ServiceOrderServicesCompleteDialog open={answerOpen} onOpenChange={setAnswerOpen} items={toAnswer} saving={m.saveResults.isPending}
        onConfirm={async (payload) => { if (await run(m.saveResults.mutateAsync(payload), 'Execução registrada')) setAnswerOpen(false); }} />
      <ServiceOrderFieldServiceDialog open={addOpen} onOpenChange={setAddOpen} products={products.data ?? []} loading={products.isLoading} saving={m.addField.isPending}
        onConfirm={async (a) => { if (await run(m.addField.mutateAsync(a), 'Serviço enviado para análise')) setAddOpen(false); }} />
      <ServiceOrderFieldServiceDialog open={manualOpen} onOpenChange={setManualOpen} products={products.data ?? []} loading={products.isLoading} saving={m.manage.isPending}
        description="O item entra na O.S. sem alterar o negócio." notesLabel="Orientação para o prestador"
        onConfirm={async (a) => { if (await run(m.manage.mutateAsync({ action: 'add', payload: { product_id: a.productId, quantity: a.quantity, operator_notes: a.notes } }), 'Item adicionado')) setManualOpen(false); }} />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar item</DialogTitle>
            <DialogDescription>{editing?.product_name} · o negócio não é alterado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Quantidade</p>
              <Input inputMode="decimal" maxLength={7} disabled={!editQtyAllowed} value={editQty} onChange={(e) => setEditQty(e.target.value.replace(/[^\d.,]/g, ''))} />
              {!editQtyAllowed && <p className="text-xs text-muted-foreground">Quantidade bloqueada: item já respondido ou status da O.S. não permite.</p>}
            </div>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Orientação para o prestador</p>
              <Textarea rows={3} maxLength={500} disabled={!canNotes} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Aparece no app do prestador e no desktop" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button disabled={m.manage.isPending || (editQtyAllowed && !(editQtyNum > 0 && editQtyNum <= 9999))} onClick={async () => {
              if (!editing) return;
              const payload: Record<string, unknown> = { id: editing.id };
              if (editQtyAllowed) payload.quantity = editQtyNum;
              if (canNotes) payload.operator_notes = editNotes;
              if (await run(m.manage.mutateAsync({ action: 'update', payload }), 'Item atualizado')) setEditing(null);
            }}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover item?</AlertDialogTitle>
            <AlertDialogDescription>{removing?.product_name} sai desta O.S. O negócio não é alterado e a atualização do negócio não traz o item de volta.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction disabled={m.manage.isPending} onClick={async (e) => {
              e.preventDefault();
              if (removing && await run(m.manage.mutateAsync({ action: 'remove', payload: { id: removing.id } }), 'Item removido')) setRemoving(null);
            }}>Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recusar serviço adicional?</AlertDialogTitle>
            <AlertDialogDescription>Informe o motivo. O negócio não é alterado.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo obrigatório..." />
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction disabled={!reason.trim() || m.review.isPending} onClick={async (e) => {
              e.preventDefault();
              if (rejecting && await run(m.review.mutateAsync({ serviceId: rejecting.id, decision: 'rejected', reason }), 'Serviço recusado')) setRejecting(null);
            }}>Recusar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Lista de serviços com situação, observação e motivo. */
function ItemsList({ title, items, renderActions }: { title: string; items: ServiceOrderServiceItem[]; renderActions?: (i: ServiceOrderServiceItem) => React.ReactNode }) {
  return (
    <section className="space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      {items.map((i) => (
        <div key={i.id} className={cn('rounded-lg border p-3', i.removed_from_deal && 'opacity-70')}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="min-w-0 truncate text-sm font-medium">{i.product_name} <span className="text-muted-foreground">· {Number(i.quantity)}x</span></p>
            <div className="flex flex-wrap items-center gap-1.5">
              {i.removed_from_deal && <Badge variant="outline" className="text-[10px]">Removido do negócio</Badge>}
              {i.locked_by_operator && <Badge variant="outline" className="text-[10px]">Editado</Badge>}
              <Badge variant="secondary" className={cn('text-[10px]', EXEC_TONE[i.execution_status])}>{SERVICE_EXECUTION_LABEL[i.execution_status]}</Badge>
              {i.approval_status && <Badge variant="secondary" className={cn('text-[10px]', APPROVAL_TONE[i.approval_status])}>{SERVICE_APPROVAL_LABEL[i.approval_status]}</Badge>}
              {renderActions?.(i)}
            </div>
          </div>
          {i.operator_notes && <p className="mt-1 text-xs text-primary"><Info className="mr-1 inline h-3 w-3" />Orientação: {i.operator_notes}</p>}
          {i.not_done_reason && <p className="mt-1 text-xs text-destructive"><X className="mr-1 inline h-3 w-3" />Motivo: {i.not_done_reason}</p>}
          {i.notes && <p className="mt-1 text-xs text-muted-foreground">Obs.: {i.notes}</p>}
          {i.approval_reason && <p className="mt-1 text-xs text-muted-foreground">Análise: {i.approval_reason}</p>}
        </div>
      ))}
    </section>
  );
}
